import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { BillingService, BillingSummary } from '@/pages/service/core/billing.service';
import { hasAnyRole } from './role.util';

/**
 * Slim, app-wide credit warning shown to the company admin (the person who can
 * top up) whenever the wallet is low or overdrawn. Self-fetches the billing
 * summary on load and re-checks as the admin navigates (throttled), so it
 * appears as soon as a send/campaign drains the balance. Dismissible per status.
 */
@Component({
    selector: 'app-billing-banner',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="bb" *ngIf="visible" [ngClass]="summary?.status === 'OVERDRAWN' ? 'bb--danger' : 'bb--warn'">
        <i class="pi" [ngClass]="summary?.status === 'OVERDRAWN' ? 'pi-exclamation-triangle' : 'pi-info-circle'"></i>
        <span class="bb-msg" *ngIf="summary?.status === 'OVERDRAWN'">
            Your credit balance is empty — sending is paused. Top up to resume messages and AI replies.
        </span>
        <span class="bb-msg" *ngIf="summary?.status === 'LOW'">
            Your credit balance is running low (₹{{ summary?.balance | number:'1.0-2' }} left). Top up soon to avoid interruptions.
        </span>
        <button class="bb-cta" (click)="viewBilling()">View billing</button>
        <button class="bb-x" (click)="dismiss()" aria-label="Dismiss"><i class="pi pi-times"></i></button>
    </div>
    `,
    styles: [`
        .bb { display:flex; align-items:center; gap:10px; padding:9px 14px; margin:0 0 14px; border-radius:var(--gh-r-sm); font-size:var(--gh-fs-14); border:1px solid transparent; }
        .bb > i { font-size:var(--gh-fs-16); flex:0 0 auto; }
        .bb-msg { flex:1 1 auto; line-height:1.35; }
        .bb--danger { background:rgba(220,38,38,.08); border-color:rgba(220,38,38,.35); color:var(--gh-danger); }
        .bb--warn { background:rgba(217,119,6,.08); border-color:rgba(217,119,6,.35); color:var(--gh-warn); }
        .bb-cta { flex:0 0 auto; background:transparent; border:1px solid currentColor; color:inherit; font-weight:600; font-size:var(--gh-fs-13); padding:4px 12px; border-radius:var(--gh-r-xs); cursor:pointer; }
        .bb-cta:hover { background:rgba(0,0,0,.05); }
        .bb-x { flex:0 0 auto; background:transparent; border:none; color:inherit; cursor:pointer; opacity:.65; padding:2px 4px; line-height:1; }
        .bb-x:hover { opacity:1; }
    `]
})
export class AppBillingBanner implements OnInit, OnDestroy {
    summary: BillingSummary | null = null;

    private isAdmin = false;
    private dismissedStatus: string | null = null;
    private lastFetch = 0;
    private sub?: Subscription;

    constructor(private billing: BillingService, private router: Router) {}

    ngOnInit(): void {
        this.isAdmin = hasAnyRole('ROLE_COMPANY_ADMIN');
        if (!this.isAdmin) return;
        this.refresh();
        // Re-check as the admin moves around — at most once a minute.
        this.sub = this.router.events
            .pipe(filter(e => e instanceof NavigationEnd))
            .subscribe(() => { if (Date.now() - this.lastFetch > 60000) this.refresh(); });
    }

    ngOnDestroy(): void { this.sub?.unsubscribe(); }

    get visible(): boolean {
        const s = this.summary?.status;
        return this.isAdmin && (s === 'LOW' || s === 'OVERDRAWN') && s !== this.dismissedStatus;
    }

    private refresh(): void {
        this.lastFetch = Date.now();
        this.billing.getSummary().subscribe({
            next: (s) => {
                this.summary = s ?? null;
                // Recovered → forget any earlier dismissal so a future dip re-alerts.
                if (this.summary?.status === 'HEALTHY') this.dismissedStatus = null;
            },
            error: (e) => {
                // Visible reason when nothing shows: 404 = backend not restarted with
                // the new /summary endpoint, 500 = credit tables not migrated yet.
                console.warn('[billing-banner] /api/credits/summary unavailable (status ' + (e?.status ?? '?') + ')');
            }
        });
    }

    viewBilling(): void {
        this.router.navigate(['/settings'], { queryParams: { tab: 'billing' } });
    }

    dismiss(): void {
        this.dismissedStatus = this.summary?.status ?? null;
    }
}
